import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth, formatErr } from "../context/AuthContext";
import { toast } from "sonner";
import { Loader2, Smartphone, Lock, Mail, KeyRound, ArrowRight, Delete, LogOut } from "lucide-react";

/* Personal mobile PWA entry — NOT tied to furgoni.
   State machine:
     1) No device_token in localStorage → "setup" screen (email+password+PIN)
     2) device_token exists → load session from server:
          a) ok → "pin" screen (big numeric keypad)
          b) 401 → clear localStorage, back to setup
*/
const DT_KEY = "energyworld.mobile_device_token";
const PIN_LEN = 4;

export default function MobileEntry() {
  const navigate = useNavigate();
  const { user, refresh } = useAuth();
  const [mode, setMode] = useState("loading"); // loading | setup | pin
  const [session, setSession] = useState(null); // { name, picture, role }

  const dt = typeof window !== "undefined" ? localStorage.getItem(DT_KEY) : null;

  useEffect(() => {
    (async () => {
      // If already logged in via normal auth and no device_token, send them to their home
      if (!dt && user) { redirectForRole(user.role, navigate); return; }
      if (!dt) { setMode("setup"); return; }
      try {
        const r = await api.post("/mobile/session", { device_token: dt });
        setSession(r.data);
        setMode("pin");
      } catch {
        localStorage.removeItem(DT_KEY);
        setMode("setup");
      }
    })();
    // eslint-disable-next-line
  }, []);

  const onUnpair = () => {
    if (!window.confirm("Rimuovere l'associazione di questo cellulare? Dovrai inserire di nuovo email e password.")) return;
    localStorage.removeItem(DT_KEY);
    setSession(null);
    setMode("setup");
  };

  if (mode === "loading") return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-950 to-slate-900 flex items-center justify-center">
      <Loader2 className="w-10 h-10 text-white animate-spin"/>
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-950 to-slate-900 text-white safe-page">
      <div className="max-w-md mx-auto px-5 pt-10 pb-6 flex flex-col min-h-screen">
        <div className="text-center mb-6">
          <img src="/assets/logo.png" alt="Energy World"
               className="w-20 h-20 mx-auto mb-3 rounded-2xl shadow-xl bg-white p-2"/>
          <h1 className="heading text-3xl font-extrabold tracking-tight">Energy World</h1>
          <p className="text-blue-200 text-sm mt-1 flex items-center justify-center gap-1">
            <Smartphone className="w-4 h-4"/> Mobile · Personale
          </p>
        </div>

        {mode === "setup" ? (
          <SetupForm onPaired={(data) => {
            localStorage.setItem(DT_KEY, data.device_token);
            toast.success(`Cellulare associato a ${data.user.name}`);
            refresh().then(() => redirectForRole(data.user.role, navigate));
          }}/>
        ) : (
          <PinPad session={session} dt={dt}
                  onSuccess={async (userData) => {
                    toast.success(`Bentornato ${userData.name.split(" ")[0]}`);
                    await refresh();
                    redirectForRole(userData.role, navigate);
                  }}
                  onUnpair={onUnpair}/>
        )}
      </div>
    </div>
  );
}

function redirectForRole(role, navigate) {
  if (role === "operaio") navigate("/lavori");
  else if (role === "personale" || role === "admin") navigate("/admin");
  else if (role === "cliente") navigate("/dashboard");
  else navigate("/");
}

function SetupForm({ onPaired }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [pin, setPin] = useState("");
  const [pin2, setPin2] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!email || !password) { toast.error("Inserisci email e password"); return; }
    if (!/^\d{4,6}$/.test(pin)) { toast.error("Il PIN deve essere 4-6 cifre"); return; }
    if (pin !== pin2) { toast.error("I due PIN non coincidono"); return; }
    setBusy(true);
    try {
      const r = await api.post("/mobile/register",
        { email: email.toLowerCase().trim(), password, pin,
          device_label: typeof navigator !== "undefined" ? navigator.userAgent.slice(0, 60) : "" });
      onPaired(r.data);
    } catch (e) { toast.error(formatErr(e)); } finally { setBusy(false); }
  };

  return (
    <div className="bg-white/10 backdrop-blur-md rounded-2xl p-5 border border-white/10 space-y-4"
         data-testid="mobile-setup">
      <div className="flex items-center gap-2 text-blue-100 text-sm">
        <Lock className="w-4 h-4"/> Associa questo cellulare al tuo account
      </div>
      <p className="text-blue-50/80 text-xs">
        Solo la <b>prima volta</b>: inserisci le tue credenziali e scegli un PIN.
        Le prossime volte entrerai col solo PIN.
      </p>

      <div>
        <label className="text-[10px] uppercase tracking-widest text-blue-200 mb-1 block">Email</label>
        <div className="relative">
          <Mail className="w-4 h-4 absolute left-3 top-3.5 text-blue-300"/>
          <input type="email" autoComplete="email" value={email}
                 onChange={e => setEmail(e.target.value)}
                 className="w-full bg-slate-900/60 border border-white/20 rounded-lg pl-9 pr-3 py-3 outline-none focus:border-blue-400"
                 placeholder="mario.rossi@energyworld.store"
                 data-testid="mobile-email"/>
        </div>
      </div>

      <div>
        <label className="text-[10px] uppercase tracking-widest text-blue-200 mb-1 block">Password</label>
        <div className="relative">
          <KeyRound className="w-4 h-4 absolute left-3 top-3.5 text-blue-300"/>
          <input type="password" autoComplete="current-password" value={password}
                 onChange={e => setPassword(e.target.value)}
                 className="w-full bg-slate-900/60 border border-white/20 rounded-lg pl-9 pr-3 py-3 outline-none focus:border-blue-400"
                 placeholder="••••••••"
                 data-testid="mobile-password"/>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="text-[10px] uppercase tracking-widest text-blue-200 mb-1 block">Nuovo PIN (4 cifre)</label>
          <input type="tel" inputMode="numeric" maxLength={6} value={pin}
                 onChange={e => setPin(e.target.value.replace(/\D/g, "").slice(0, 6))}
                 className="w-full text-center text-2xl tracking-[0.4em] font-bold bg-slate-900/60 border border-white/20 rounded-lg py-3 outline-none focus:border-blue-400"
                 placeholder="····" data-testid="mobile-new-pin"/>
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-widest text-blue-200 mb-1 block">Conferma PIN</label>
          <input type="tel" inputMode="numeric" maxLength={6} value={pin2}
                 onChange={e => setPin2(e.target.value.replace(/\D/g, "").slice(0, 6))}
                 className="w-full text-center text-2xl tracking-[0.4em] font-bold bg-slate-900/60 border border-white/20 rounded-lg py-3 outline-none focus:border-blue-400"
                 placeholder="····" data-testid="mobile-new-pin2"/>
        </div>
      </div>

      <button
        onClick={submit} disabled={busy}
        className="w-full bg-blue-500 hover:bg-blue-400 disabled:opacity-40 text-white font-semibold py-4 rounded-xl flex items-center justify-center gap-2 transition"
        data-testid="mobile-setup-submit">
        {busy ? <Loader2 className="w-5 h-5 animate-spin"/> : <>Associa cellulare <ArrowRight className="w-5 h-5"/></>}
      </button>

      <p className="text-center text-xs text-blue-200/70">
        Non hai un account? <a href="/login" className="underline">Accedi dal browser</a>
      </p>
    </div>
  );
}

function PinPad({ session, dt, onSuccess, onUnpair }) {
  const [pin, setPin] = useState("");
  const [busy, setBusy] = useState(false);
  const busyRef = useRef(false);

  const submit = async (thePin) => {
    if (busyRef.current) return;
    busyRef.current = true; setBusy(true);
    try {
      const r = await api.post("/mobile/pin-login", { device_token: dt, pin: thePin });
      onSuccess(r.data);
    } catch (e) {
      setPin("");
      toast.error(formatErr(e));
    } finally { busyRef.current = false; setBusy(false); }
  };

  const press = (d) => {
    if (busy || pin.length >= 6) return;
    const next = pin + d;
    setPin(next);
    if (next.length === PIN_LEN) submit(next);
  };
  const back = () => setPin(p => p.slice(0, -1));

  const roleBadge = session?.role === "admin" ? "bg-amber-500" :
                    session?.role === "personale" ? "bg-blue-500" : "bg-slate-400";

  return (
    <div className="flex-1 flex flex-col justify-center" data-testid="mobile-pinpad">
      <div className="text-center mb-6">
        {session?.picture ? (
          <img src={session.picture} alt={session.name}
               referrerPolicy="no-referrer"
               className="w-20 h-20 rounded-full mx-auto mb-3 border-2 border-white/30 object-cover"/>
        ) : (
          <div className="w-20 h-20 rounded-full mx-auto mb-3 bg-white/10 border-2 border-white/30 flex items-center justify-center text-3xl font-bold">
            {(session?.name || "?").split(" ").map(s => s[0]).slice(0,2).join("")}
          </div>
        )}
        <div className="heading text-xl font-bold" data-testid="mobile-user-name">
          Ciao {session?.name?.split(" ")[0]}
        </div>
        <div className={`inline-block mt-1 text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full ${roleBadge} text-white font-semibold`}>
          {session?.role}
        </div>
      </div>

      <div className="flex justify-center gap-3 mb-6" data-testid="mobile-pin-dots">
        {Array.from({ length: PIN_LEN }).map((_, i) => (
          <div key={i}
               className={`w-4 h-4 rounded-full border-2 border-white/50 ${i < pin.length ? "bg-white" : "bg-transparent"}`}/>
        ))}
      </div>

      <div className="grid grid-cols-3 gap-3 max-w-xs mx-auto w-full" data-testid="mobile-pin-keypad">
        {[1,2,3,4,5,6,7,8,9].map(n => (
          <button key={n} disabled={busy} onClick={() => press(String(n))}
                  className="text-3xl font-bold py-5 rounded-2xl bg-white/10 hover:bg-white/20 active:bg-white/25 border border-white/10 transition active:scale-95 disabled:opacity-40"
                  data-testid={`mobile-pin-${n}`}>
            {n}
          </button>
        ))}
        <button onClick={onUnpair} disabled={busy}
                className="text-[11px] font-semibold py-5 rounded-2xl bg-transparent hover:bg-white/10 text-blue-200"
                data-testid="mobile-unpair">
          Cambia account
        </button>
        <button disabled={busy} onClick={() => press("0")}
                className="text-3xl font-bold py-5 rounded-2xl bg-white/10 hover:bg-white/20 active:bg-white/25 border border-white/10 transition active:scale-95 disabled:opacity-40"
                data-testid="mobile-pin-0">
          0
        </button>
        <button disabled={busy || pin.length === 0} onClick={back}
                className="flex items-center justify-center py-5 rounded-2xl bg-white/5 hover:bg-white/15 border border-white/10 transition disabled:opacity-40"
                data-testid="mobile-pin-del">
          <Delete className="w-6 h-6"/>
        </button>
      </div>

      {busy && <div className="mt-5 inline-flex items-center justify-center gap-2 text-blue-200 text-sm w-full"><Loader2 className="w-4 h-4 animate-spin"/> Verifica...</div>}
    </div>
  );
}
